// Dashboard placeholder — toont user-info + logout. Echte ASO komt in v0.3 (PRD-03 §3.1).
// Voor client-role: redirect naar /onboarding als wizard niet COMPLETED.

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { authStore } from "@/lib/auth";
import { useAuth } from "@/hooks/useAuth";
import { EmberChat } from "@/components/ember/EmberChat";

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Redirect client-rol naar onboarding-wizard als nog niet COMPLETED
  const onboardingQuery = useQuery({
    queryKey: ["onboarding-progress"],
    queryFn: () => api.get<{ status: string }>("/v1/onboarding/progress"),
    enabled: user?.role === "client",
    retry: false,
  });

  useEffect(() => {
    if (user?.role === "client" && onboardingQuery.data && onboardingQuery.data.status !== "COMPLETED") {
      navigate("/onboarding", { replace: true });
      return;
    }
    if (user?.role === "municipality") {
      navigate("/municipality", { replace: true });
      return;
    }
    if (user?.role === "counselor") {
      navigate("/counselor", { replace: true });
    }
  }, [user?.role, onboardingQuery.data, navigate]);

  async function handleLogout() {
    const refreshToken = authStore.getRefreshToken();
    try {
      if (refreshToken) {
        await api.post("/v1/auth/logout", { refreshToken }, { skipAuth: true });
      }
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
    } finally {
      authStore.clear();
      navigate("/login", { replace: true });
    }
  }

  return (
    <main className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-neutral-900">Balans Buddy</h1>
          <div className="flex items-center gap-4">
            {user && ["admin", "super_admin"].includes(user.role) && (
              <Link
                to="/admin"
                className="px-3 py-1.5 text-sm text-brand-600 hover:text-brand-800 rounded-lg"
              >
                {t("admin.entrypoint", "Beheer")}
              </Link>
            )}
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 text-sm text-neutral-700 hover:text-neutral-900 rounded-lg"
            >
              {t("logout", "Uitloggen")}
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl border border-neutral-200 p-8">
          <h2 className="text-2xl font-semibold mb-2">
            {t("common.dashboard.welcome", "Welkom")}, {user?.email}
          </h2>
          <p className="text-neutral-600">
            {t("common.dashboard.placeholder", "ASO-dashboard komt hier (PRD-03 §3.1).")}
          </p>

          <dl className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-neutral-500">{t("common.dashboard.role", "Rol")}</dt>
              <dd className="font-medium">{user?.role}</dd>
            </div>
            <div>
              <dt className="text-neutral-500">{t("common.dashboard.tenant", "Tenant")}</dt>
              <dd className="font-medium font-mono text-xs">{user?.tenantId}</dd>
            </div>
          </dl>
        </div>
      </section>

      <EmberChat />
    </main>
  );
}
